import { ReloadOutlined } from '@ant-design/icons';
import { Button, Card, Col, message as antdMessage, Modal, Row, Space, Table } from 'antd';
import dayjs from 'dayjs';
import type { TFunction } from 'i18next';
import { useCallback, useEffect, useState } from 'react';
import { useApi } from '../../contexts/ApiContext';
import {
    type MetalCategory,
    type MetalCategoryDates,
    metalCategoryLabelKey,
    metalDateKey,
    type MetalInventoryDate,
    type MetalInventoryItem,
    type MetalSelections,
    orderMetalCategories,
    pruneMetalSelections,
    toMetalDateSelections,
} from '../../domain/metals/metalCategories';

type Props = {
    t: TFunction;
    /** Changes whenever the page wants the lists read again (the operator came back to the tab). */
    refreshKey: number;
};

const format2 = (value: number | string): string => Number(value ?? 0).toFixed(2);

const itemColumns = (t: TFunction) => [
    { key: 'contract', title: t('metals.contract'), dataIndex: 'contract_number' },
    { key: 'client', title: t('metals.client'), dataIndex: 'client_name' },
    { key: 'purity', title: t('metals.karat'), dataIndex: 'purity', render: format2 },
    { key: 'qty', title: t('metals.qty'), dataIndex: 'quantity_grams', render: format2 },
    { key: 'value', title: t('metals.valueRon'), dataIndex: 'value_ron', render: format2 },
];

const dateColumns = (t: TFunction) => [
    {
        key: 'date',
        title: t('metals.inventoryDate'),
        dataIndex: 'date',
        render: (date: string) => dayjs(date).format('YYYY-MM-DD'),
    },
    { key: 'items', title: t('metals.byMetal.items'), dataIndex: 'items' },
    { key: 'grams', title: t('metals.qty'), dataIndex: 'grams', render: format2 },
];

/** The objects of an expanded date; still loading while `items` is undefined. */
const itemsTable = (items: MetalInventoryItem[] | undefined, t: TFunction) => (
    <Table<MetalInventoryItem>
        size="small"
        rowKey="itemId"
        dataSource={items ?? []}
        columns={itemColumns(t)}
        loading={!items}
        pagination={false}
    />
);

function IncomingByMetalContent({ t, refreshKey }: Readonly<Props>) {
    const { metals } = useApi();
    const [messageApi, contextHolder] = antdMessage.useMessage();
    const [modal, modalContextHolder] = Modal.useModal();
    const [categories, setCategories] = useState<MetalCategoryDates[]>(orderMetalCategories([]));
    const [loading, setLoading] = useState(false);
    const [selections, setSelections] = useState<MetalSelections>({});
    const [items, setItems] = useState<Record<string, MetalInventoryItem[]>>({});

    const loadCategories = useCallback(async () => {
        setLoading(true);
        const res = await metals.getIncomingByMetal();
        if (res.success) {
            const ordered = orderMetalCategories(res.data?.categories ?? []);
            setCategories(ordered);
            setSelections(previous => pruneMetalSelections(previous, ordered));
            setItems({});
        } else {
            messageApi.error(res.error ?? t('metals.byMetal.loadFailed'));
        }
        setLoading(false);
    }, [metals, messageApi, t]);

    useEffect(() => {
        void loadCategories();
    }, [loadCategories, refreshKey]);

    const loadItems = async (metalType: MetalCategory, date: string) => {
        const key = metalDateKey(metalType, date);
        if (items[key]) return;
        const res = await metals.getIncomingByMetalItems(date, metalType);
        // A failed load ends the spinner with an empty list; reloading the tab tries again.
        setItems(previous => ({ ...previous, [key]: res.success ? (res.data?.items ?? []) : [] }));
        if (!res.success) {
            messageApi.error(res.error ?? t('metals.byMetal.loadFailed'));
        }
    };

    const selected = toMetalDateSelections(selections);

    const addSelected = () =>
        metals.addToInventoryByMetal(selected).then(res => {
            if (!res.success) {
                messageApi.error(res.error ?? t('metals.byMetal.addFailed'));
                return;
            }
            messageApi.success(t('metals.byMetal.added', { count: res.data?.added ?? 0 }));
            setSelections({});
            return loadCategories();
        });

    const confirmAdd = () => {
        modal.confirm({
            title: t('metals.byMetal.confirmTitle'),
            content: t('metals.byMetal.confirmContent', { count: selected.length }),
            centered: true,
            onOk: addSelected,
        });
    };

    const renderCategory = ({ metalType, dates }: MetalCategoryDates) => (
        <Col key={metalType} xs={24} md={12} xl={6}>
            <Card
                size="small"
                title={t(metalCategoryLabelKey(metalType))}
                data-testid={`by-metal-${metalType}`}
            >
                <Table<MetalInventoryDate>
                    size="small"
                    rowKey="date"
                    dataSource={dates}
                    columns={dateColumns(t)}
                    loading={loading}
                    pagination={{ pageSize: 10, size: 'small', hideOnSinglePage: true }}
                    locale={{ emptyText: t('metals.byMetal.empty') }}
                    rowSelection={{
                        selectedRowKeys: selections[metalType] ?? [],
                        onChange: keys =>
                            setSelections(previous => ({
                                ...previous,
                                [metalType]: keys.map(String),
                            })),
                    }}
                    expandable={{
                        expandedRowRender: record =>
                            itemsTable(items[metalDateKey(metalType, record.date)], t),
                        onExpand: (expanded, record) => {
                            if (expanded) void loadItems(metalType, record.date);
                        },
                    }}
                />
            </Card>
        </Col>
    );

    return (
        <div>
            {contextHolder}
            {modalContextHolder}
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
                <Space>
                    <Button
                        icon={<ReloadOutlined />}
                        onClick={() => void loadCategories()}
                        aria-label={t('metals.byMetal.refresh')}
                    />
                    <Button type="primary" onClick={confirmAdd} disabled={selected.length === 0}>
                        {t('metals.byMetal.add')}
                    </Button>
                </Space>
            </div>
            <Row gutter={[16, 16]}>{categories.map(renderCategory)}</Row>
        </div>
    );
}

export default IncomingByMetalContent;
